/**
 * Auto-Add Gift Product Manager
 *
 * Automatically adds a gift product to the cart when:
 * 1. Any trigger product exists in the cart
 * 2. Ensures the gift product is added only once (no duplications)
 *
 * @class GiftProductManager
 */
class GiftProductManager {
    /**
     * Creates an instance of GiftProductManager
     * @param {Object} config - Configuration object
     * @param {Object} config.giftProduct - The gift product object
     * @param {Array} config.triggerProducts - Array of trigger product IDs
     * @param {boolean} config.autoAddForAnySubs - Whether to add gift for any subscription
     * @param {Array} config.triggerSubsProducts - Array of trigger subscription product IDs
     * @param {boolean} config.stopAutoRemovingGiftProduct - Whether to stop auto removing gift product
     * @param {boolean} config.isFirstOrderEligible - True only if a logged-in customer has 0 prior orders. Guests = false.
     */
    constructor(config) {
        if (!config || !config.giftProduct || !config.triggerProducts) {
            console.warn("[GiftProductManager] Invalid configuration provided");
            return;
        }

        this.giftProduct = config.giftProduct;
        this.triggerProducts = config.triggerProducts;
        this.autoAddForAnySubs = config.autoAddForAnySubs;
        this.triggerSubsProducts = config.triggerSubsProducts;
        this.stopAutoRemovingGiftProduct = config.stopAutoRemovingGiftProduct;
        this.isFirstOrderEligible = config.isFirstOrderEligible === true;
        this.giftVariantId = this.getGiftVariantId();

        // ─────────────────────────────────────────────────────────────────────
        // HARDCODED VARIANT CARVE-OUT
        // Shopify theme settings only let merchants pick trigger *products*, not
        // specific variants. These variant IDs are excluded from triggering the
        // gift even though their parent product may be configured as a trigger.
        // If one of these variants is the only trigger-eligible item in the cart,
        // the gift will NOT be auto-added.
        //
        // products/10844635726158/variants/55169815314766
        // ─────────────────────────────────────────────────────────────────────
        this.excludedTriggerVariantIds = [];

        // State management
        this.isProcessing = false;
        this.lastCartState = null;
        this.isRemovingGift = false;

        // Debounce timer
        this.checkTimer = null;
        this.DEBOUNCE_DELAY = 300; // ms

        if (!this.giftVariantId) {
            console.error("[GiftProductManager] Could not determine gift variant ID");
            return;
        }

        // if (!this.triggerProducts || this.triggerProducts.length === 0) {
        //     console.warn("[GiftProductManager] No trigger products configured");
        //     return;
        // }

        this.init();
    }

    /**
     * Get the variant ID of the gift product
     * @returns {number|null} Variant ID or null
     */
    getGiftVariantId() {
        try {
            // Try to get the first available variant
            if (this.giftProduct.variants && this.giftProduct.variants.length > 0) {
                return this.giftProduct.variants[0].id;
            }
            // Fallback to id property
            if (this.giftProduct.id) {
                return this.giftProduct.id;
            }
            return null;
        } catch (error) {
            console.error(
                "[GiftProductManager] Error getting gift variant ID:",
                error
            );
            return null;
        }
    }

    /**
     * Whether a given cart line item is on the hardcoded exclusion list.
     * Excluded variants never count as trigger products.
     * @param {Object} item - Cart line item
     * @returns {boolean}
     */
    isExcludedTriggerVariant(item) {
        if (!item) return false;
        return this.excludedTriggerVariantIds.includes(item.variant_id);
    }

    /**
     * Initialize the manager and attach event listeners
     */
    init() {
        // console.log('[GiftProductManager] Initialized', {
        //     giftVariantId: this.giftVariantId,
        //     triggerProductCount: this.triggerProducts.length
        // });

        // Listen to cart update events
        if (
            typeof subscribe === "function" &&
            typeof PUB_SUB_EVENTS !== "undefined"
        ) {
            subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
                this.debouncedCheck();
            });
        }

        // Initial check on page load
        this.debouncedCheck();
    }

    /**
     * Debounced cart check to prevent excessive API calls
     */
    debouncedCheck() {
        clearTimeout(this.checkTimer);
        this.checkTimer = setTimeout(() => {
            this.checkAndUpdateCart();
        }, this.DEBOUNCE_DELAY);
    }

    /**
     * Main logic: Check cart state and update gift product accordingly
     */
    async checkAndUpdateCart() {
        // Prevent concurrent executions — schedule another pass so we don't miss updates
        if (this.isProcessing) {
            this.debouncedCheck();
            return;
        }

        this.isProcessing = true;

        try {
            let cart = await this.fetchCart();

            if (!cart) {
                console.warn("[GiftProductManager] Failed to fetch cart");
                return;
            }

            let hasTriggerProduct = this.hasTriggerProduct(cart);
            let hasGiftProduct = this.hasGiftInCart(cart);
            let hasNoGiftAttribute = cart.attributes && cart.attributes["No Gift"] && cart.attributes["No Gift"].trim() !== "";

            const hadTriggerBefore =
                this.lastCartState && typeof this.lastCartState.hadTriggerProduct === "boolean"
                    ? this.lastCartState.hadTriggerProduct
                    : null;

            // Re-added a trigger after cart had none (e.g. removed subscription then added again).
            // "No Gift" is only cleared when !hasTriggerProduct elsewhere, so a stale attribute
            // would block the gift forever while subscription is in cart.
            if (
                hadTriggerBefore === false &&
                hasTriggerProduct &&
                hasNoGiftAttribute
            ) {
                const updatedCart = await this.setCartAttribute("No Gift", "");
                if (updatedCart) {
                    cart = updatedCart;
                    hasNoGiftAttribute =
                        cart.attributes &&
                        cart.attributes["No Gift"] &&
                        cart.attributes["No Gift"].trim() !== "";
                }
            }

            // Check if gift was manually removed.
            // IMPORTANT: Only treat it as "manual removal" if trigger products still exist.
            // Otherwise, we may incorrectly mark "No Gift" after we auto-remove the gift
            // (e.g. when the subscription/trigger is removed).
            const previousHadGift = this.lastCartState && this.lastCartState.hasGift;
            let attributeWasSet = false;
            if (previousHadGift && !hasGiftProduct && !this.isRemovingGift && hasTriggerProduct) {
                // Gift was manually removed - set "No Gift" attribute
                const updatedCart = await this.setCartAttribute("No Gift", "true");
                if (updatedCart) {
                    // Update cart object with the response from setting the attribute
                    cart = updatedCart;
                    attributeWasSet = true;
                }
            }

            // Clear "No Gift" attribute if no trigger products are in cart
            if (!hasTriggerProduct && hasNoGiftAttribute) {
                const updatedCart = await this.setCartAttribute("No Gift", "");
                if (updatedCart) {
                    // Update cart object with the response from clearing the attribute
                    cart = updatedCart;
                }
            }

            // Re-check cart state if we updated attributes
            if (attributeWasSet) {
                hasTriggerProduct = this.hasTriggerProduct(cart);
                hasGiftProduct = this.hasGiftInCart(cart);
                hasNoGiftAttribute = cart.attributes && cart.attributes["No Gift"] && cart.attributes["No Gift"].trim() !== "";
            }


            const shouldHaveGift = hasTriggerProduct && !hasNoGiftAttribute && this.isFirstOrderEligible;

            if (shouldHaveGift && !hasGiftProduct) {
                // Add gift product
                await this.addGiftToCart();
            } else if ((!hasTriggerProduct || !this.isFirstOrderEligible) && hasGiftProduct) {
                // Remove gift product (trigger products removed OR shopper is not first-order eligible)
                await this.removeGiftFromCart(cart);
            }
            // else: already in correct state, do nothing

            // Store current cart state for next comparison (after all operations complete)
            // Store a minimal state object to track gift presence
            this.lastCartState = {
                hadTriggerProduct: hasTriggerProduct,
                hasGift: hasGiftProduct,
                itemCount: cart.item_count,
                items: cart.items ? cart.items.map(item => ({
                    variant_id: item.variant_id,
                    product_id: item.product_id
                })) : []
            };
        } catch (error) {
            console.error("[GiftProductManager] Error in checkAndUpdateCart:", error);
        } finally {
            this.isProcessing = false;
        }
    }

    /**
     * Fetch the current cart state from Shopify
     * @returns {Promise<Object|null>} Cart object or null
     */
    async fetchCart() {
        try {
            const response = await fetch(window.routes?.cart_url || "/cart.js", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error("[GiftProductManager] Error fetching cart:", error);
            return null;
        }
    }

    /**
     * Check if any trigger product exists in the cart
     * @param {Object} cart - Cart object
     * @returns {boolean}
     */
    hasTriggerProduct(cart) {
        if (!cart || !cart.items || cart.items.length === 0) {
            return false;
        }

        const triggerProductsIds = this.triggerProducts.map(
            (product) => product.id
        );

        // Get trigger subscription product IDs if available
        const triggerSubsProductsIds = this.triggerSubsProducts
            ? this.triggerSubsProducts.map((product) => product.id)
            : [];

        return cart.items.some((item) => {
            // HARDCODED CARVE-OUT: excluded variants can never trigger the gift,
            // regardless of whether their parent product is a configured trigger
            // or the line is a subscription.
            if (this.isExcludedTriggerVariant(item)) return false;

            // Check by product_id (regular trigger products)
            if (triggerProductsIds.includes(item.product_id)) return true;

            // Check if item is a subscription (has selling_plan_allocation)
            const isSubscription = !!item.selling_plan_allocation;

            if (isSubscription) {
                // If autoAddForAnySubs is enabled, any subscription triggers the gift
                if (this.autoAddForAnySubs) return true;

                // Check if this subscription product is in the trigger subscription products list
                if (triggerSubsProductsIds.includes(item.product_id)) return true;
            }

            return false;
        });
    }

    /**
     * Check if the gift product is already in the cart
     * @param {Object} cart - Cart object
     * @returns {boolean}
     */
    hasGiftInCart(cart) {
        if (!cart || !cart.items || cart.items.length === 0) {
            return false;
        }

        // Only treat the gift as "managed" if it was auto-added by this script.
        // Otherwise, customers can't buy the gift product as a normal standalone item
        // because it would be removed whenever no trigger product exists.
        return cart.items.some((item) => this.isAutoAddedGiftLineItem(item));
    }

    /**
     * Determine whether a cart item is the auto-added gift line item.
     * @param {Object} item - Cart line item
     * @returns {boolean}
     */
    isAutoAddedGiftLineItem(item) {
        if (!item) return false;
        if (item.variant_id !== this.giftVariantId) return false;
        const props = item.properties || {};
        return (
            props["_added_by"] === "auto-gift-manager" ||
            props["_gift_product"] === "true" ||
            props["_gift_product"] === true
        );
    }

    /**
     * Set a cart attribute
     * @param {string} attributeName - Name of the attribute
     * @param {string} attributeValue - Value of the attribute (empty string to remove)
     * @returns {Promise<void>}
     */
    async setCartAttribute(attributeName, attributeValue) {
        try {
            const formData = new FormData();
            formData.append(`attributes[${attributeName}]`, attributeValue);

            const response = await fetch("/cart/update.js", {
                method: "POST",
                body: formData,
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error("[GiftProductManager] Error setting cart attribute:", error);
            // Don't throw - fail gracefully
        }
    }

    /**
     * Add the gift product to the cart
     * Uses the same pattern as product-form.js: request sections and call renderContents
     * @returns {Promise<void>}
     */
    async addGiftToCart() {
        try {
            // Get reference to cart drawer (same as product-form.js)
            const cart =
                document.querySelector("cart-notification") ||
                document.querySelector("cart-drawer");

            // Build FormData like product-form.js does
            const formData = new FormData();
            formData.append("id", this.giftVariantId);
            formData.append("quantity", 1);
            formData.append("properties[_gift_product]", "true");
            formData.append("properties[_added_by]", "auto-gift-manager");

            // Append sections to render (same as product-form.js lines 48-52)
            if (cart && typeof cart.getSectionsToRender === "function") {
                formData.append(
                    "sections",
                    cart.getSectionsToRender().map((section) => section.id)
                );
                formData.append("sections_url", window.location.pathname);
            }

            // Use global routes and fetchConfig (same as product-form.js)
            const url = routes?.cart_add_url || "/cart/add.js";
            const config =
                typeof fetchConfig === "function"
                    ? fetchConfig("javascript")
                    : {
                        method: "POST",
                        headers: { "X-Requested-With": "XMLHttpRequest" },
                    };

            config.headers["X-Requested-With"] = "XMLHttpRequest";
            delete config.headers["Content-Type"]; // Let browser set it for FormData
            config.body = formData;

            const response = await fetch(url, config);
            const result = await response.json();

            if (result.status) {
                // Error response
                throw new Error(result.description || "Failed to add gift product");
            }

            console.log('[GiftProductManager] ✅ Gift product added successfully', result);

            // Clear "No Gift" attribute since gift was successfully added
            console.log('[GiftProductManager] 🧹 Clearing "No Gift" attribute (gift was added)');
            await this.setCartAttribute("No Gift", "");

            // Trigger cart update event
            if (
                typeof publish === "function" &&
                typeof PUB_SUB_EVENTS !== "undefined"
            ) {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: "auto-gift-manager" });
            }

            // Call renderContents to update cart drawer (same as product-form.js line 96)
            if (cart && typeof cart.renderContents === "function") {
                cart.renderContents(result);
            }
        } catch (error) {
            console.error("[GiftProductManager] Error adding gift to cart:", error);
            // Don't throw - fail gracefully
        }
    }

    /**
     * Remove the gift product from the cart
     * Leverages existing CartItems.updateQuantity() for consistent behavior
     * @param {Object} cart - Cart object
     * @returns {Promise<void>}
     */
    async removeGiftFromCart(cart) {
        try {
            if (this.stopAutoRemovingGiftProduct) return;

            // Set flag to indicate we're removing the gift ourselves
            this.isRemovingGift = true;

            // Find the line item index for the gift product
            const giftItemIndex = cart.items.findIndex((item) =>
                this.isAutoAddedGiftLineItem(item)
            );

            // if (cart.items.length === 1 && giftItemIndex !== -1) return;

            if (giftItemIndex === -1) {
                this.isRemovingGift = false;
                return; // Gift not in cart
            }

            // console.log('[GiftProductManager] Removing gift product from cart');

            // Line number is 1-indexed in Shopify API
            const lineNumber = giftItemIndex + 1;

            // Try to use existing CartItems.updateQuantity() method
            const cartItems =
                document.querySelector("cart-items") ||
                document.querySelector("cart-drawer-items");

            if (cartItems && typeof cartItems.updateQuantity === "function") {
                // Use existing cart update flow - handles section rendering, is-empty toggling, PubSub
                // console.log('[GiftProductManager] Using CartItems.updateQuantity for removal');
                cartItems.updateQuantity(lineNumber, 0, null);
                // Note: updateQuantity triggers async cart update event
                // Reset flag after a delay to allow cart update to complete
                setTimeout(() => {
                    this.isRemovingGift = false;
                }, this.DEBOUNCE_DELAY + 100); // Slightly longer than debounce delay
            } else {
                // Fallback: Manual removal if CartItems not available
                // console.log('[GiftProductManager] CartItems not found, using fallback removal');
                await this.fallbackRemoveGift(lineNumber);
                // Reset flag immediately for fallback since we await the operation
                this.isRemovingGift = false;
            }
        } catch (error) {
            console.error(
                "[GiftProductManager] Error removing gift from cart:",
                error
            );
            // Reset flag on error
            this.isRemovingGift = false;
            // Don't throw - fail gracefully
        }
    }

    /**
     * Fallback method to remove gift if CartItems is not available
     * Uses same pattern as product-form.js for section rendering
     * @param {number} lineNumber - Line number (1-indexed)
     * @returns {Promise<void>}
     */
    async fallbackRemoveGift(lineNumber) {
        try {
            // Get reference to cart drawer (same as product-form.js)
            const cart =
                document.querySelector("cart-notification") ||
                document.querySelector("cart-drawer");

            // Build FormData like product-form.js does
            const formData = new FormData();
            formData.append("line", lineNumber);
            formData.append("quantity", 0);

            // Append sections to render (same as product-form.js)
            if (cart && typeof cart.getSectionsToRender === "function") {
                formData.append(
                    "sections",
                    cart.getSectionsToRender().map((section) => section.id)
                );
                formData.append("sections_url", window.location.pathname);
            }

            // Use global routes and fetchConfig
            const url = routes?.cart_change_url || "/cart/change.js";
            const config =
                typeof fetchConfig === "function"
                    ? fetchConfig("javascript")
                    : {
                        method: "POST",
                        headers: { "X-Requested-With": "XMLHttpRequest" },
                    };

            config.headers["X-Requested-With"] = "XMLHttpRequest";
            delete config.headers["Content-Type"]; // Let browser set it for FormData
            config.body = formData;

            const response = await fetch(url, config);
            const result = await response.json();

            // console.log('[GiftProductManager] Gift product removed successfully (fallback)');

            // Trigger cart update event
            if (
                typeof publish === "function" &&
                typeof PUB_SUB_EVENTS !== "undefined"
            ) {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: "auto-gift-manager" });
            }

            // Call renderContents to update cart drawer (same as product-form.js)
            if (cart && typeof cart.renderContents === "function") {
                cart.renderContents(result);
            }
        } catch (error) {
            console.error("[GiftProductManager] Error in fallback removal:", error);
        }
    }
}

// Initialize on DOMContentLoaded
document.addEventListener("DOMContentLoaded", function () {
    // Check if configuration exists
    if (window.Shopify && window.Shopify.autoAddGiftToCart) {
        const config = window.Shopify.autoAddGiftToCart;

        // Create singleton instance
        window.giftProductManager = new GiftProductManager(config);
    } else {
        // console.log('[GiftProductManager] Configuration not found, skipping initialization');
    }
});